import { t } from '../../i18n.es.js';
import React, { useMemo } from 'react';
import { styled, Currency } from '@teko/ui';
import { Flex, Tag, Shape } from '@teko/ds-foundation';

const Price = styled.div`
  font-weight: 700;
  font-size: 14px;
  padding: 0;
  text-align: right;
  vertical-align: top;
`;
const FinalPrice = styled.div`
  color: #eb2101;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1;
  text-align: right;
  vertical-align: bottom;
  padding: 0;
`;
const Text = styled.div`
  font-size: 14px;
  color: #848788;
  margin-top: 4px;
  text-align: center;
`;
var InvoiceSectionKey = /* @__PURE__ */ ((InvoiceSectionKey2) => {
  InvoiceSectionKey2[InvoiceSectionKey2["SubTotal"] = 1] = "SubTotal";
  InvoiceSectionKey2[InvoiceSectionKey2["AddedPrice"] = 2] = "AddedPrice";
  InvoiceSectionKey2[InvoiceSectionKey2["ShippingFee"] = 3] = "ShippingFee";
  InvoiceSectionKey2[InvoiceSectionKey2["OtherFees"] = 4] = "OtherFees";
  InvoiceSectionKey2[InvoiceSectionKey2["Discount"] = 5] = "Discount";
  InvoiceSectionKey2[InvoiceSectionKey2["GrandTotal"] = 6] = "GrandTotal";
  InvoiceSectionKey2[InvoiceSectionKey2["ShippingFeeDiscount"] = 7] = "ShippingFeeDiscount";
  return InvoiceSectionKey2;
})(InvoiceSectionKey || {});
const OrderDetailInvoices = ({
  orderDetail,
  textColor = "#848788",
  vat,
  className,
  infoText,
  transformSections
}) => {
  var _a;
  let invoices = [];
  const { grandTotal, shippingFee, subTotal, unitAdd, serviceFee, promotions } = orderDetail || {};
  const couponText = useMemo(() => {
    const coupons = promotions == null ? void 0 : promotions.map((promo) => promo.coupon).filter((c) => c);
    if (coupons == null ? void 0 : coupons.length) {
      return (coupons == null ? void 0 : coupons.length) === 1 ? coupons[0] : `${coupons.length} ${t("M\xC3 GI\u1EA2M GI\xC1")}`;
    } else {
      return null;
    }
  }, [promotions]);
  const discount = (promotions == null ? void 0 : promotions.reduce((total, promo) => {
    if (["order", "part_order"].includes((promo == null ? void 0 : promo.applyType) || "")) {
      return total + ((promo == null ? void 0 : promo.discount) || 0);
    }
    return total;
  }, 0)) || 0;
  const shippingFeeDiscount = (promotions == null ? void 0 : promotions.reduce((total, promo) => {
    if ((promo == null ? void 0 : promo.applyType) === "service_fee") {
      return total + ((promo == null ? void 0 : promo.discount) || 0);
    }
    return total;
  }, 0)) || 0;
  invoices = [
    {
      key: 1 /* SubTotal */,
      text: t("T\u1ED5ng t\u1EA1m t\xEDnh"),
      price: subTotal || 0
    },
    unitAdd && {
      key: 2 /* AddedPrice */,
      text: t("T\u1ED5ng n\xE2ng gi\xE1"),
      price: unitAdd
    },
    shippingFee !== void 0 && {
      key: 3 /* ShippingFee */,
      text: t("Ph\xED v\u1EADn chuy\u1EC3n"),
      price: `${shippingFee || 0}`
    },
    !!shippingFeeDiscount && !!shippingFee && {
      key: 7 /* ShippingFeeDiscount */,
      text: t("Gi\u1EA3m gi\xE1 ph\xED v\u1EADn chuy\u1EC3n"),
      price: shippingFeeDiscount > shippingFee ? -shippingFee : -shippingFeeDiscount
    },
    !!((_a = serviceFee == null ? void 0 : serviceFee.optional) == null ? void 0 : _a.length) && {
      key: 4 /* OtherFees */,
      children: serviceFee.optional.map((fee) => ({
        text: fee.name,
        price: fee.rowTotal
      }))
    },
    {
      key: 5 /* Discount */,
      text: /* @__PURE__ */ React.createElement(Flex, {
        align: "center",
        justify: "end"
      }, t("Gi\u1EA3m gi\xE1"), " \xA0", couponText && /* @__PURE__ */ React.createElement(Tag, {
        content: couponText,
        type: "ghost",
        variant: "error"
      })),
      price: -discount
    },
    {
      key: 6 /* GrandTotal */,
      text: t("Th\xE0nh ti\u1EC1n"),
      price: grandTotal || 0
    }
  ].filter((invoice) => !!invoice);
  invoices = transformSections ? transformSections(invoices) : invoices;
  invoices = invoices.map((line, index) => {
    if (line.children) {
      return /* @__PURE__ */ React.createElement(React.Fragment, {
        key: line.key
      }, line.children.map((fee, feeIndex) => {
        return /* @__PURE__ */ React.createElement(Flex, {
          key: "fee_" + feeIndex,
          justify: "space-between",
          paddingTop: "s8"
        }, /* @__PURE__ */ React.createElement(Flex, {
          color: textColor,
          align: "center"
        }, fee.text), /* @__PURE__ */ React.createElement(Price, null, /* @__PURE__ */ React.createElement(Currency, {
          value: fee.price,
          space: true,
          underline: true
        })));
      }));
    } else {
      return /* @__PURE__ */ React.createElement(Flex, {
        justify: "space-between",
        paddingTop: "s8",
        key: line.key
      }, /* @__PURE__ */ React.createElement(Flex, {
        color: textColor,
        align: "center"
      }, line.text), index !== invoices.length - 1 ? /* @__PURE__ */ React.createElement(Price, null, line.noFormat ? line.price : /* @__PURE__ */ React.createElement(Currency, {
        value: line.price,
        space: true,
        underline: true
      })) : /* @__PURE__ */ React.createElement(FinalPrice, null, /* @__PURE__ */ React.createElement(Currency, {
        value: line.price,
        space: true
      })));
    }
  });
  return /* @__PURE__ */ React.createElement(Flex, {
    className,
    direction: "column",
    align: "end"
  }, /* @__PURE__ */ React.createElement(PriceSummaryWrapper, null, invoices), vat && /* @__PURE__ */ React.createElement(Text, null, t("(\u0110\xE3 bao g\u1ED3m VAT{{var0}})", {
    var0: (orderDetail == null ? void 0 : orderDetail.shippingFee) === void 0 ? ", Chi\u1EBFt Kh\u1EA5u v\xE0 ph\xED giao h\xE0ng n\u1EBFu c\xF3" : ""
  })), !vat && (orderDetail == null ? void 0 : orderDetail.shippingFee) === void 0 && /* @__PURE__ */ React.createElement(Text, null, t("(\u0110\xE3 bao g\u1ED3m ph\xED giao h\xE0ng)")), !!infoText && /* @__PURE__ */ React.createElement(Text, null, infoText));
};
const PriceSummaryWrapper = styled(Shape)`
  display: inline-block;
  min-width: 300px;
`;

export { InvoiceSectionKey, OrderDetailInvoices };
